import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  isDevMode,
  inject,
  provideAppInitializer,
  LOCALE_ID,
} from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeVi from '@angular/common/locales/vi';

// Vietnamese number/currency formatting (thousands = ".", decimal = ",") so `| number`
// renders 15.000 instead of 15,000 across the app.
registerLocaleData(localeVi);
import {
  provideRouter,
  withPreloading,
  PreloadAllModules,
  NoPreloading,
  withInMemoryScrolling,
} from '@angular/router';

import { routes } from './app.routes';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { apiPrefixInterceptor } from './core/http/interceptors/api-prefix.interceptor';
import { authInterceptor } from './core/auth/auth.interceptor';
import { loadingInterceptor } from './core/http/interceptors/loading.interceptor';
import { NgxMaskConfig, provideEnvironmentNgxMask } from 'ngx-mask';
import { TranslateModule } from '@ngx-translate/core';
import { APP_CONFIG } from './core/config/config.service';
import { appConfig as config } from './core/config/app.config';
import { credentialsInterceptor } from './core/http/interceptors/credentials.interceptor';
import { csrfInterceptor } from './core/http/interceptors/csrf.interceptor';
import { errorHandlerInterceptor } from './core/http/interceptors/error-handler.interceptor';
import { retryInterceptor } from './core/http/interceptors/retry.interceptor';
import { frontendErrorReportingInterceptor } from './core/http/interceptors/frontend-error-reporting.interceptor';
import { timeoutInterceptor } from './core/http/interceptors/timeout.interceptor';
import { orgContextInterceptor } from './core/http/interceptors/org-context.interceptor';
import { appLocationInterceptor } from './core/http/interceptors/app-location.interceptor';
import { GlobalErrorHandler } from './core/errors/global-error-handler';
import { provideServiceWorker } from '@angular/service-worker';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { TitleStrategy } from '@angular/router';
import { TranslatedTitleStrategy } from './core/services/translated-title.strategy';
import { AppearanceService } from './core/services/appearance.service';
import { AppUpdateService } from './core/services/app-update.service';
import { IconCanaryService } from './shared/services/icon-canary.service';

const maskConfigFunction: () => Partial<NgxMaskConfig> = () => {
  return {
    validation: true,
    thousandSeparator: ',',
    clearIfNotMatch: false,
    decimalMarker: '.',
  };
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withPreloading(isDevMode() ? NoPreloading : PreloadAllModules),
      // Enable anchor scrolling so [fragment]="..." links scroll to <a id="..."> targets
      // (component library uses this for ds-* component anchor navigation).
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      }),
    ),
    provideAnimations(),
    provideHttpClient(
      withInterceptors([
        credentialsInterceptor,
        orgContextInterceptor,
        // TRƯỚC apiPrefixInterceptor: chỉ lời gọi tương đối (API của app) mới mang header vị trí.
        appLocationInterceptor,
        loadingInterceptor,
        apiPrefixInterceptor,
        csrfInterceptor,
        authInterceptor,
        errorHandlerInterceptor,
        frontendErrorReportingInterceptor,
        retryInterceptor,
        timeoutInterceptor,
      ]),
    ),
    importProvidersFrom([TranslateModule.forRoot()]),
    provideNativeDateAdapter(),
    provideEnvironmentNgxMask(maskConfigFunction),
    { provide: APP_CONFIG, useValue: config },
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerImmediately',
    }),
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: {
        usePopover: false, // Set to false to revert to the old z-index method
      },
    },
    { provide: TitleStrategy, useClass: TranslatedTitleStrategy },
    { provide: LOCALE_ID, useValue: 'vi' },
    // Lỗi runtime ⇒ console + báo lên Giám sát (core/errors/global-error-handler.ts).
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    // Eagerly instantiate AppearanceService before first paint so density / font-size
    // CSS variables are applied to :root before the router renders /login. Without
    // this, login on a cold cache renders with SCSS-default heights and only jumps
    // to the user's persisted density once another component (e.g. top-navbar)
    // injects the service after a successful login.
    provideAppInitializer(() => {
      inject(AppearanceService);
    }),
    // Canary icon — chỉ sống ở dev. Bắt tên icon KHÔNG có glyph đang render ra chữ thô, kể cả
    // tên do backend/DB quyết (xnk-hub lấy `item.icon` thẳng từ GetXnkHubSummaryHandler.cs),
    // thứ mà cả regex lẫn trình biên dịch đều không thể biết. Xem icon-canary.service.ts.
    provideAppInitializer(() => {
      void inject(IconCanaryService).start();
    }),
    // Nghe ngsw để nhắc người dùng tải lại khi đã có bản mới, và để cứu trạng thái
    // `unrecoverable`. Không có nó, bản deploy mới chỉ tới tay người dùng khi họ đóng
    // hẳn app rồi mở lại — điều gần như không xảy ra với một PWA đã cài.
    // Xem doc-comment của AppUpdateService.
    provideAppInitializer(() => {
      inject(AppUpdateService).init();
    }),
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'fill',
        hideRequiredMarker: false, // M4: Always show asterisk (*) on required fields
      },
    },
  ],
};
